import React, { useState, useEffect } from "react";
import api from "../../utils/api/axiosInstance";
import { FaGraduationCap, FaCheckCircle, FaClock, FaTimesCircle } from "react-icons/fa";
import Loader from "../common/Loader";
import styles from "../../styles/components/profile/ExpertApplicationSection.module.css";

const STATUS_COPY = {
  none: null,
  pending: { icon: FaClock, label: "Application Under Review", color: "#0246C2", bg: "#E8F1FF" },
  approved: { icon: FaCheckCircle, label: "Verified Expert", color: "#16a34a", bg: "#dcfce7" },
  rejected: { icon: FaTimesCircle, label: "Application Not Approved", color: "#dc2626", bg: "#fee2e2" },
};

const ExpertApplicationSection = ({ showToast }) => {
  const [status, setStatus] = useState(null);
  const [eligible, setEligible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bio, setBio] = useState("");
  const [specialties, setSpecialties] = useState("");
  const [requirements, setRequirements] = useState(null);

  useEffect(() => {
    api.get("/expert/status")
      .then((res) => {
        setStatus(res.data.expert?.status || "none");
        setEligible(res.data.eligible);
        setRequirements(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/expert/apply", {
        bio,
        specialties: specialties.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setStatus("pending");
      showToast({ message: "Application submitted! We'll review it within 48 hours.", type: "success" });
    } catch (err) {
      showToast({ message: err?.response?.data?.message || "Could not submit application", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loader inline={true} />;

  const statusInfo = STATUS_COPY[status];

  return (
    <div className={styles.section}>
      <div className={styles.intro}>
        <FaGraduationCap className={styles.introIcon} />
        <div>
          <strong>Become a Verified Expert</strong>
          <p>
            Verified Experts get a special badge on all their reviews, appear in expert spotlights,
            and unlock a higher credibility tier on the platform.
          </p>
        </div>
      </div>

      {statusInfo && (
        <div className={styles.statusBanner} style={{ background: statusInfo.bg, color: statusInfo.color }}>
          <statusInfo.icon />
          <span>{statusInfo.label}</span>
        </div>
      )}

      {status === "none" || status === "rejected" ? (
        eligible ? (
          <form onSubmit={handleApply} className={styles.form}>
            <label className={styles.label}>
              Why should you be a Verified Expert? <span className={styles.charCount}>{bio.length}/280</span>
            </label>
            <textarea
              className={styles.textarea}
              value={bio}
              onChange={(e) => setBio(e.target.value.slice(0, 280))}
              placeholder="Share your expertise, experience, and what categories you specialize in..."
              rows={4}
              required
            />
            <label className={styles.label}>
              Specialties <span className={styles.hint}>(comma-separated, e.g. cameras, audio, laptops)</span>
            </label>
            <input
              type="text"
              className={styles.input}
              value={specialties}
              onChange={(e) => setSpecialties(e.target.value)}
              placeholder="cameras, audio, laptops"
            />
            <button type="submit" className={styles.applyBtn} disabled={submitting || !bio.trim()}>
              {submitting ? "Submitting…" : "Submit Application"}
            </button>
          </form>
        ) : (
          <div className={styles.notEligible}>
            <p>You're not yet eligible to apply. Requirements:</p>
            <ul>
              <li className={requirements?.tier === "gold" || requirements?.tier === "platinum" ? styles.met : ""}>
                Gold or Platinum tier {requirements?.tier && `(yours: ${requirements.tier})`}
              </li>
              <li className={(requirements?.helpfulVotes || 0) >= 10 ? styles.met : ""}>
                10+ helpful votes on reviews {requirements?.helpfulVotes !== undefined && `(yours: ${requirements.helpfulVotes})`}
              </li>
              <li className={(requirements?.reviewsWritten || 0) >= 15 ? styles.met : ""}>
                15+ reviews written {requirements?.reviewsWritten !== undefined && `(yours: ${requirements.reviewsWritten})`}
              </li>
            </ul>
          </div>
        )
      ) : null}
    </div>
  );
};

export default ExpertApplicationSection;
